/**
 * The pipeline, always the same steps in the same order.
 *
 * The one loud element on the page, because it answers the only question anyone has while a
 * build runs: where is it, and if it stopped, where. Segments are equal width -- the build step
 * takes most of the wall clock, so proportional widths would show a bar and five slivers.
 *
 * Six steps for most builds. A seventh appears only for an iOS build that was asked to deliver,
 * because a strip that always showed it would imply every build was going to Apple.
 */
import type { StepState } from '../useJobStream';

/** Exit codes come straight from wl/errors.py; they are what a failed job reports. */
const STEPS: { name: string; label: string; exit: number }[] = [
  { name: 'preflight', label: 'Preflight', exit: 2 },
  { name: 'checkout', label: 'Checkout', exit: 2 },
  { name: 'rebrand', label: 'Rebrand', exit: 2 },
  { name: 'build', label: 'Build', exit: 3 },
  { name: 'verify', label: 'Verify', exit: 4 },
  { name: 'smoke', label: 'Smoke test', exit: 5 },
  { name: 'deliver', label: 'App Store', exit: 9 },
];

function elapsed(step: StepState | undefined): string {
  if (!step) return '';
  if (step.status === 'skip') return 'skipped';
  if (step.seconds === undefined) return '';
  return step.seconds >= 60
    ? `${Math.floor(step.seconds / 60)}m ${String(Math.round(step.seconds % 60)).padStart(2, '0')}s`
    : `${step.seconds.toFixed(1)}s`;
}

export function StepStrip({ steps, active }: { steps: Record<string, StepState>; active: string | null }) {
  // Only a build that is delivering has this step at all; the rest end at the smoke test.
  const STRIP = steps.deliver || active === 'deliver'
    ? STEPS
    : STEPS.filter((step) => step.name !== 'deliver');
  const failedAt = STRIP.findIndex((step) => steps[step.name]?.status === 'fail');

  return (
    <div className="strip">
      {STRIP.map((step, index) => {
        const state = steps[step.name];
        const isActive = active === step.name;
        // After a failure the remaining steps never ran; showing them level with the rest
        // would imply they were skipped by choice.
        const unreached = failedAt !== -1 && index > failedAt;
        const className = ['stage', isActive ? 'active' : state?.status ?? '',
          (!state && !isActive) || unreached ? 'pending' : ''].filter(Boolean).join(' ');

        return (
          <div key={step.name} className={className}>
            <span className="stage-name">{step.label}</span>
            <span className="stage-time">{isActive ? 'running' : elapsed(state)}</span>
            {state?.status === 'fail' && <span className="stage-exit">exit {step.exit}</span>}
          </div>
        );
      })}
    </div>
  );
}
